import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class CaskSubLocation extends ModelObjectWithDirtyCheck<CaskSubLocation> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    public isHidden: boolean = false;
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isHidden = jsonSerializedObject.isHidden;
          this.name = jsonSerializedObject.name;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.isHidden = this.isHidden;
        toRet.name = this.name;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "CaskSubLocation.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class CaskSubSubLocation extends ModelObjectWithDirtyCheck<CaskSubSubLocation> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    public isHidden: boolean = false;
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isHidden = jsonSerializedObject.isHidden;
          this.name = jsonSerializedObject.name;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.isHidden = this.isHidden;
        toRet.name = this.name;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "CaskSubSubLocation.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class CaskLocationService extends ServiceBase {

  public async getAllCaskSubLocations(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(CaskSubLocation)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.caskSubLocations.map((v: any) => new CaskSubLocation(v));;
    return this.makeJsonRequest("caskLocation/v1/getAllCaskSubLocations", req, false, extractor, opts);
  };

  public async getCaskSubLocation(caskSubLocationId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<CaskSubLocation> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskSubLocationId:caskSubLocationId.toString(),
    };
    const extractor = (response:any) => new CaskSubLocation(response.caskSubLocation);;
    return this.makeJsonRequest("caskLocation/v1/getCaskSubLocation", req, false, extractor, opts);
  };

  public async getCaskSubLocations(caskSubLocationIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(CaskSubLocation)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskSubLocationIds:caskSubLocationIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.caskSubLocations.map((v: any) => new CaskSubLocation(v));;
    return this.makeJsonRequest("caskLocation/v1/getCaskSubLocations", req, false, extractor, opts);
  };

  public async saveCaskSubLocation(caskSubLocation: CaskSubLocation, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskSubLocation:caskSubLocation.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("caskLocation/v1/saveCaskSubLocation", req, true, extractor, opts);
  };

  public async setCaskSubLocationIsHidden(caskSubLocationId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskSubLocationId:caskSubLocationId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("caskLocation/v1/setCaskSubLocationIsHidden", req, true, extractor, opts);
  };

  public async getAllCaskSubSubLocations(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(CaskSubSubLocation)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.caskSubSubLocations.map((v: any) => new CaskSubSubLocation(v));;
    return this.makeJsonRequest("caskLocation/v1/getAllCaskSubSubLocations", req, true, extractor, opts);
  };

  public async getCaskSubSubLocation(caskSubSubLocationId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<CaskSubSubLocation> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskSubSubLocationId:caskSubSubLocationId.toString(),
    };
    const extractor = (response:any) => new CaskSubSubLocation(response.caskSubSubLocation);;
    return this.makeJsonRequest("caskLocation/v1/getCaskSubSubLocation", req, false, extractor, opts);
  };

  public async getCaskSubSubLocations(caskSubSubLocationIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(CaskSubSubLocation)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskSubSubLocationIds:caskSubSubLocationIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.caskSubSubLocations.map((v: any) => new CaskSubSubLocation(v));;
    return this.makeJsonRequest("caskLocation/v1/getCaskSubSubLocations", req, false, extractor, opts);
  };

  public async saveCaskSubSubLocation(caskSubSubLocation: CaskSubSubLocation, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskSubSubLocation:caskSubSubLocation.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("caskLocation/v1/saveCaskSubSubLocation", req, true, extractor, opts);
  };

  public async setCaskSubSubLocationIsHidden(caskSubSubLocationId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    caskSubSubLocationId:caskSubSubLocationId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("caskLocation/v1/setCaskSubSubLocationIsHidden", req, true, extractor, opts);
  };
}

export const caskLocationService = new CaskLocationService();